/**
 * Request and response shapes of the mai-work-report web API, shared by the server (validation)
 * and the web app. Instants are ISO 8601 strings in UTC; dates and week starts are `YYYY-MM-DD`
 * (weeks start on Monday, in the report owner's time zone).
 */
import { DAYS_PER_WEEK, ReportSection, ReportStatus, zIsoDate, zWorkDaySummary, } from '@mai-team-amusement/core';
import { DateTime } from 'luxon';
import { z } from 'zod';
import { zRichText } from './rich-text.js';
export * from './rich-text.js';
/** Weeks shown in the team overview by default, and at most. */
export const TEAM_WEEKS_DEFAULT = 4;
export const TEAM_WEEKS_MAX = 12;
export const HISTORY_LIMIT = 104;
export const DEFAULT_AUDIT_PAGE_SIZE = 50;
export const MAX_AUDIT_PAGE_SIZE = 200;
const MONDAY = 1;
const zInstant = z.iso.datetime();
/** A Monday. */
export const zWeekStart = zIsoDate.refine((date) => DateTime.fromISO(date).weekday === MONDAY, 'must be a Monday');
/** How a revision came about. */
export const RevisionKind = {
    /** Saved by the report's owner. */
    Edited: 1,
    /** "Plan for this week" copied from the previous week's "plan for next week". */
    Inherited: 2,
};
/** State of one member's week in the team overview. */
export const TeamReportState = {
    Missing: 0,
    Unconfirmed: 1,
    Confirmed: 2,
};
export const zMemberView = z.object({
    sub: z.string(),
    familyName: z.string(),
    givenName: z.string(),
    employeeNumber: z.string(),
    groupId: z.number().int(),
    groupName: z.string().nullable(),
    timeZone: z.string(),
});
export const zPersonRef = z.object({ sub: z.string(), name: z.string().nullable() });
export const zDaySection = z.object({ date: zIsoDate, content: zRichText });
/** The nine sections of a report. */
export const zReportSections = z.object({
    planThisWeek: zRichText,
    days: z.array(zDaySection).length(DAYS_PER_WEEK),
    planNextWeek: zRichText,
});
/** `version`: of the report as loaded, `null` when it did not exist yet (optimistic locking). */
export const zSaveReportInput = zReportSections.extend({ version: z.number().int().nullable() });
export const zReportView = zReportSections.extend({
    user: zMemberView,
    weekStart: zIsoDate,
    /** Zone of the report (snapshot at creation; the owner's zone before that). */
    timeZone: z.string(),
    /** Changes are possible until this instant (Monday 12:00 of the next week, local time). */
    lockAt: zInstant,
    /** False until the first save; the sections are then a prefilled draft. */
    exists: z.boolean(),
    version: z.number().int().nullable(),
    status: z.enum(ReportStatus),
    confirmedBy: zPersonRef.nullable(),
    confirmedAt: zInstant.nullable(),
    updatedAt: zInstant.nullable(),
    /** "Plan for this week" was copied from last week's "plan for next week" (not saved yet). */
    inherited: z.boolean(),
    /** Recorded work days of the week from mai-work-clock; `null` when it is unavailable. */
    workDays: z.array(zWorkDaySummary).nullable(),
    /** What the signed-in user may do with this report now. */
    permissions: z.object({
        edit: z.boolean(),
        confirm: z.boolean(),
        unconfirm: z.boolean(),
    }),
});
export const zRevisionView = z.object({
    id: z.number().int(),
    section: z.enum(ReportSection),
    /** The day, for `ReportSection.Day`. */
    date: zIsoDate.nullable(),
    kind: z.enum(RevisionKind),
    before: zRichText.nullable(),
    after: zRichText,
    editor: zPersonRef.nullable(),
    createdAt: zInstant,
});
export const zRevisionList = z.object({ revisions: z.array(zRevisionView) });
export const zHistoryEntry = z.object({
    weekStart: zIsoDate,
    status: z.enum(ReportStatus),
    lockAt: zInstant,
    confirmedAt: zInstant.nullable(),
    updatedAt: zInstant,
});
export const zHistory = z.object({ user: zMemberView, reports: z.array(zHistoryEntry) });
export const zTeamQuery = z.object({
    groupId: z.coerce.number().int().positive().optional(),
    weeks: z.coerce.number().int().min(1).max(TEAM_WEEKS_MAX).default(TEAM_WEEKS_DEFAULT),
});
export const zTeamWeek = z.object({
    weekStart: zIsoDate,
    state: z.enum(TeamReportState),
    /** Past the change deadline. */
    locked: z.boolean(),
});
export const zTeamMember = z.object({ user: zMemberView, weeks: z.array(zTeamWeek) });
export const zTeamReports = z.object({ weeks: z.array(zIsoDate), members: z.array(zTeamMember) });
export const zGroupList = z.object({
    groups: z.array(z.object({ id: z.number().int(), name: z.string() })),
});
export const zAuditQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_AUDIT_PAGE_SIZE)
        .default(DEFAULT_AUDIT_PAGE_SIZE),
});
export const zAuditEntry = z.object({
    id: z.number().int(),
    actorUserId: z.string().nullable(),
    actorName: z.string().nullable(),
    action: z.number().int(),
    targetType: z.string(),
    targetId: z.string(),
    before: z.unknown(),
    after: z.unknown(),
    ip: z.string().nullable(),
    createdAt: zInstant,
});
export const zAuditPage = z.object({ items: z.array(zAuditEntry), total: z.number().int() });
export const ReportApiPath = {
    Week: '/api/v1/reports/:sub/weeks/:weekStart',
    Confirm: '/api/v1/reports/:sub/weeks/:weekStart/confirm',
    Unconfirm: '/api/v1/reports/:sub/weeks/:weekStart/unconfirm',
    Revisions: '/api/v1/reports/:sub/weeks/:weekStart/revisions',
    History: '/api/v1/reports/:sub/weeks',
    Team: '/api/v1/team/reports',
    Groups: '/api/v1/groups',
    AuditLogs: '/api/v1/admin/audit-logs',
};
/** Fills `:name` placeholders of an API path. */
export function apiPath(path, params) {
    return path.replace(/:(\w+)/g, (placeholder, name) => {
        const value = params[name];
        return value === undefined ? placeholder : encodeURIComponent(String(value));
    });
}
//# sourceMappingURL=index.js.map